import { useState, useEffect } from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Avatar from "@mui/material/Avatar";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Icon } from "@iconify/react";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useTheme } from "@mui/material/styles";

import {
  peopleDataState,
  AccountInfo,
  selectedAccountsState,
  reloadPeopleData,
  searchState,
  selectedCategory,
  storyFilterBoxState,
  negateLabelsState,
  projectPickerState,
} from "./JotaiAtoms";

import * as lodash from "lodash";

function AccountRow({ accounts }: { accounts: AccountInfo[] }) {
  const theme = useTheme();
  const [selectedFlag, setSelectedFlag] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const setSelected = useSetAtom(selectedAccountsState);
  const reload = useAtomValue(reloadPeopleData);
  const setSearch = useSetAtom(searchState);
  const setSelectedCategory = useSetAtom(selectedCategory);
  const setGlobalFilters = useSetAtom(storyFilterBoxState);
  const setNegate = useSetAtom(negateLabelsState);
  const project = useAtomValue(projectPickerState);

  useEffect(() => {
    setSelectedFlag(false);
  }, [reload]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectedFlag(checked);

    if (checked) {
      setSelected((prev: AccountInfo[][]) => [...prev, accounts]);
    } else {
      setSelected((prev: AccountInfo[][]) => {
        const i = prev.findIndex((e) => lodash.isEqual(e, accounts));
        if (i === -1) return prev;
        return prev.slice(0, i).concat(prev.slice(i + 1));
      });
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();

    setGlobalFilters((prev: any) => {
      if (!prev) return prev;
      if (!prev[project] || prev[project].length === 0) return prev;
      return { ...prev, [project]: [] };
    });

    setNegate(false);

    setTimeout(() => {
      setSelectedCategory("Stories");
      setSearch([accounts]);
    }, 0);
  };

  const getAvatar = () => {
    for (const acc of accounts) {
      if (acc.avatar) return acc.avatar;
    }
    return null;
  };

  const getName = () => {
    let name = "";
    for (const acc of accounts) {
      if (acc.name && acc.name.length > name.length) {
        name = acc.name;
      }
    }
    if (name) return name;

    // Fallback to username if no name
    const usernames = getUsernames();
    if (usernames.length > 0) return usernames[0];

    // Fallback to email if no name or username
    const emails = getEmails();
    if (emails.length > 0) return emails[0];

    return "Unknown";
  };

  const getUsernames = () => {
    const out = new Set<string>();
    for (const acc of accounts) {
      if (acc.username) out.add(acc.username);
    }
    return Array.from(out);
  };

  const getEmails = () => {
    const out = new Set<string>();
    for (const acc of accounts) {
      if (acc.email) out.add(acc.email);
    }
    return Array.from(out);
  };

  const getApps = () => {
    // Group accounts by app type
    const appGroups = new Map<string, [string, string | undefined][]>();

    for (const acc of accounts) {
      if (!appGroups.has(acc.app)) {
        appGroups.set(acc.app, []);
      }
      appGroups.get(acc.app)!.push([acc.app, acc.url]);
    }

    const out: [string, string | undefined][] = [];

    // For each app type, decide what to show
    for (const [appName, instances] of appGroups) {
      // Get unique instances with URLs
      const withUrls = instances.filter(([_, url]) => url);
      const uniqueWithUrls = Array.from(
        new Map(withUrls.map(([app, url]) => [url, [app, url]])).values()
      ) as [string, string | undefined][];

      if (uniqueWithUrls.length > 0) {
        // Show all clickable instances
        out.push(...uniqueWithUrls);
      } else {
        // No clickable instances, show one icon without URL
        out.push([appName, undefined]);
      }
    }

    return out;
  };

  const name = getName();
  const usernames = getUsernames();
  const emails = getEmails();
  const apps = getApps();
  const avatar = getAvatar();

  // Don't show username if it's already used as the name
  const displayUsernames = usernames.filter(u => u !== name);
  // Don't show email if it's already used as the name
  const displayEmails = emails.filter(e => e !== name);

  const truncateList = (items: string[], max: number) => {
    if (items.length <= max) return { visible: items, more: 0 };
    return { visible: items.slice(0, max), more: items.length - max };
  };

  const emailsTruncated = truncateList(displayEmails, 2);
  const usernamesTruncated = truncateList(displayUsernames, 3);

  const allEmails = displayEmails.join(", ");
  const allUsernames = displayUsernames.map(u => `@${u}`).join(", ");

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 0.75,
        px: 1,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)'}`,
        backgroundColor: selectedFlag
          ? theme.palette.mode === 'dark'
            ? 'rgba(100, 150, 200, 0.08)'
            : 'rgba(100, 150, 200, 0.06)'
          : 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(0, 0, 0, 0.015)',
        },
      }}
    >
      {/* Checkbox - only visible on hover or when selected */}
      <Box
        sx={{
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isHovered || selectedFlag ? 1 : 0,
          transition: 'opacity 0.15s ease-out',
        }}
      >
        <Checkbox
          checked={selectedFlag}
          onChange={handleCheckboxChange}
          size="small"
          sx={{
            p: 0,
            '& .MuiSvgIcon-root': {
              fontSize: 18,
            },
            color: 'var(--text-tertiary)',
            '&.Mui-checked': {
              color: 'var(--accent-primary)',
            },
          }}
        />
      </Box>

      {/* Avatar */}
      {avatar ? (
        <Avatar src={avatar} sx={{ width: 28, height: 28 }} />
      ) : (
        <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)' }}>
          <PersonIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}

      {/* Name & Info */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Name */}
        <Box sx={{ minWidth: 100 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {name}
            {accounts.length > 1 && (
              <Typography
                component="span"
                sx={{
                  ml: 0.5,
                  fontSize: 10,
                  fontWeight: 400,
                  color: 'var(--text-tertiary)',
                }}
              >
                ({accounts.length})
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Usernames & Emails */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {/* Usernames */}
          {usernamesTruncated.visible.map((username, i) => (
            <Typography
              key={username}
              sx={{
                fontSize: 11,
                color: 'var(--text-secondary)',
              }}
            >
              @{username}{i < usernamesTruncated.visible.length - 1 && ","}
            </Typography>
          ))}
          {usernamesTruncated.more > 0 && (
            <Tooltip title={allUsernames}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  cursor: 'help',
                }}
              >
                +{usernamesTruncated.more}
              </Typography>
            </Tooltip>
          )}

          {/* Separator */}
          {displayUsernames.length > 0 && displayEmails.length > 0 && (
            <Typography sx={{ fontSize: 11, color: 'var(--text-tertiary)' }}>•</Typography>
          )}

          {/* Emails */}
          {emailsTruncated.visible.map((email, i) => (
            <Typography
              key={email}
              sx={{
                fontSize: 10,
                color: 'var(--text-secondary)',
              }}
            >
              {email}{i < emailsTruncated.visible.length - 1 && ","}
            </Typography>
          ))}
          {emailsTruncated.more > 0 && (
            <Tooltip title={allEmails}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  cursor: 'help',
                }}
              >
                +{emailsTruncated.more}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Apps as icons or text */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5 }}>
          {apps.map(([app, url]) => {
            const lower = app.toLowerCase();

            const getIconName = () => {
              if (lower.includes('github')) return 'uil:github';
              if (lower.includes('slack')) return 'uil:slack';
              if (lower.includes('gitlab')) return 'ph:gitlab-logo-fill';
              if (lower.includes('gmail')) return 'bxl:gmail';
              if (lower.includes('jira')) return 'mdi:jira';
              return null;
            };

            const iconName = getIconName();

            if (!iconName) {
              return (
                <Typography
                  key={app + (url || "")}
                  sx={{
                    fontSize: 10,
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {url ? (
                    <Link
                      href={url}
                      sx={{
                        fontSize: 10,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {app}
                    </Link>
                  ) : (
                    app
                  )}
                </Typography>
              );
            }

            return (
              <Tooltip key={app + (url || "")} title={app}>
                {url ? (
                  <Link
                    href={url}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#64a8d8',
                      '&:hover': { color: '#4a90c0' },
                    }}
                  >
                    <Icon icon={iconName} width={16} height={16} />
                  </Link>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    <Icon icon={iconName} width={16} height={16} />
                  </Box>
                )}
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Navigate button */}
      <Tooltip title="View stories">
        <IconButton
          size="small"
          onClick={handleNavigate}
          sx={{
            p: 0.5,
            color: 'var(--text-secondary)',
            '&:hover': {
              color: 'var(--accent-primary)',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function PeopleList() {
  const data = useAtomValue(peopleDataState);
  const setSelected = useSetAtom(selectedAccountsState);
  const theme = useTheme();

  useEffect(() => {
    setSelected((prev: AccountInfo[][]) => {
      if (prev.length === 0) return prev;
      return [];
    });
  }, [data]);

  useEffect(() => {
    setSelected((prev: AccountInfo[][]) => {
      if (prev.length === 0) return prev;
      return [];
    });
  }, []);

  const groupAccounts = () => {
    const out: AccountInfo[][] = [];
    const seen = new Set<string>();

    for (const user of data.values()) {
      if (user[0].group) {
        if (!seen.has(user[0].group)) {
          seen.add(user[0].group);
          out.push(user);
        }
      } else {
        out.push(user);
      }
    }

    return out;
  };

  const accounts = groupAccounts();

  return (
    <Box sx={{ maxWidth: 900 }}>
      {accounts.map((group) => (
        <AccountRow
          key={`${group[0].app || ""}-${group[0].id || ""}-${group[0].email || ""}-${group[0].username || ""}`}
          accounts={group}
        />
      ))}
    </Box>
  );
}
